import { useState, useCallback, useEffect, useMemo } from "react";
import { Mui, MuiIcons } from '@osu/react-ui';
import { useDispatch } from 'react-redux';
import { clear } from "../../services/graphql/slices/search-slice";
import AttributeRow from "./AttributeRow";
import { SEARCH_OPERATORS } from "../../util/constants";
import { getProgramIdFromPath } from "../../util/scholars/scholarsScope";
import {
    useGetApplicantAttributesQuery,
    useLazySearchForAllDataQuery,
} from "../../services/graphql/api";
import ResultsGrid from "./ResultsGrid";

function AdvancedSearch() {
    const { data: attributesData, isFetching: attributesLoading, refetch: refetchAttributes } = useGetApplicantAttributesQuery();
    const [attributeRows, setAttributeRows] = useState([{ id: 1, index: 0 }]); 
    const [searchCriteria, setSearchCriteria] = useState(getInitialSearchCriteriaValue());
    const [paginationModel, setPaginationModel] = useState({ pageIndex: 0, pageSize: 100 });
    const [sortModel, setSortModel] = useState([{ id: 'documentCreatedDatetime', desc: false }]);
    const [cachedSearchCriteria, setCachedSearchCriteria] = useState({});
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const dispatch = useDispatch();

    const [
        triggerAllDataSearch,
        { isFetching: allDataLoading },
    ] = useLazySearchForAllDataQuery();

    // has592 — the program the search is currently scoped to, taken from the ProgramId row (row 0). The user
    // can change that row, so key off its value; fall back to the route (/scholars vs /honors) before any
    // edit. When exactly one program is selected, program-specific attributes/statuses for the OTHER program
    // are hidden. When zero or both are selected, nothing is hidden (both programs shown).
    const activeProgram = useMemo(() => {
        const programIdRow = searchCriteria[0];
        if (programIdRow?.selectedAttribute?.key === "programId") {
            const vals = Array.isArray(programIdRow.selectedValue)
                ? programIdRow.selectedValue
                : [programIdRow.selectedValue].filter(Boolean);
            if (vals.length === 1) { return vals[0]; }
            if (vals.length > 1) { return null; } // both/multiple -> don't filter
        }
        return getProgramIdFromPath();
    }, [searchCriteria]);

    // Hide the other program's program-tagged attributes from the Select Attribute dropdown. Untagged
    // attributes (program === undefined) are shared and always shown. No active program -> show all.
    const filteredAttributes = useMemo(() => {
        const all = attributesData?.attributes ?? [];
        if (!activeProgram) { return all; }
        return all.filter((attr) => !attr?.program || attr.program === activeProgram);
    }, [attributesData, activeProgram]);

    const searchParams = useMemo(() => {
        if (Object.keys(cachedSearchCriteria).length === 0) {
            return null;
        }
        return {
            criteria: cachedSearchCriteria,
            paginationModel,
            sortModel
        };
    }, [cachedSearchCriteria, paginationModel, sortModel]);

    // Use the search params to trigger searches automatically
    useEffect(() => {
        if (searchParams) {
            triggerAllDataSearch({ input: searchParams });
        }
    }, [searchParams, triggerAllDataSearch]);

    // Datagrid Refresh: re-run the current search with the exact same params (no change to
    // criteria/pagination/sort). Awaitable so the toolbar control can show its spinner until the
    // re-fetch settles. No-op until a search has been run (searchParams is null before then).
    const onRefresh = useCallback(() => {
        if (!searchParams) { return undefined; }
        return triggerAllDataSearch({ input: searchParams }).unwrap().catch(() => {});
    }, [searchParams, triggerAllDataSearch]);

    const updateRowData = useCallback((index, data) => {
        setSearchCriteria((prev) => ({
            ...prev,
            [index]: data
        }));
    }, []);

    const clearSearchCriteria = useCallback(() => {
        setSearchCriteria({});
        setAttributeRows([{ id: 1, index: 0 }]);
    }, []);

    const clearGrid = useCallback(() => {
        dispatch(clear());
        setAttributeRows([{ id: 1, index: 0 }]);
        setSearchCriteria({});
        refetchAttributes(); // Reset/refetch the attributes data
    }, [dispatch, refetchAttributes]);

    const saveSearchCriteria = useCallback(() => {
        const dataToSave = {
            attributeRows,
            searchCriteria
        };
        localStorage.setItem('savedSearchCriteria', JSON.stringify(dataToSave));
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
    }, [attributeRows, searchCriteria]);

    const loadSearchCriteria = useCallback(() => {
        const saved = localStorage.getItem('savedSearchCriteria');
        if (saved) {
            try {
                const { attributeRows: savedRows, searchCriteria: savedCriteria } = JSON.parse(saved);
                // Generate new IDs to force remount of AttributeRow components
                const newRows = (savedRows || [{ id: 1, index: 0 }]).map((row, idx) => ({
                    id: Math.random().toString(36).substring(2, 15),
                    index: idx
                }));
                setAttributeRows(newRows);
                setSearchCriteria(savedCriteria || {});
            } catch (error) {
                console.error('Failed to load saved search criteria:', error);
            }
        }
    }, []);

    function deleteRow(rowId) {

        // Find the row being deleted
        const rowToDelete = attributeRows.find(row => row.id === rowId);
        if (!rowToDelete) return;

        // Remove the row from attributeRows and reindex
        const newRows = attributeRows
            .filter(row => row.id !== rowId)
            .map((row, newIndex) => ({ ...row, index: newIndex })); // Reindex remaining rows

        setAttributeRows(newRows);

        // Remove the deleted row's data and adjust indices in search criteria
        setSearchCriteria((prev) => {
            const newCriteria = { ...prev };
            delete newCriteria[rowToDelete.index];

            // Adjust indices for remaining rows
            const adjustedCriteria = {};
            Object.keys(newCriteria).forEach(key => {
                const numKey = parseInt(key);
                if (numKey > rowToDelete.index) {
                    adjustedCriteria[numKey - 1] = newCriteria[key];
                } else {
                    adjustedCriteria[key] = newCriteria[key];
                }
            });

            return adjustedCriteria;
        });
    }

    function addNewRow() {
        const newRowId = Math.random().toString(36).substring(2, 15);
        const newIndex = attributeRows.length;
        setAttributeRows([...attributeRows, { id: newRowId, index: newIndex }]);
    }

 function handleSearch() {
        // Filter out empty criteria with better validation
        const validCriteria = Object.values(searchCriteria).filter(criteria => {
            const hasAttribute = criteria.selectedAttribute && criteria.selectedAttribute !== '';
            const hasOperator = criteria.operator && criteria.operator !== '';

            const hasValue = criteria.selectedValue !== null && criteria.selectedValue !== undefined && criteria.selectedValue.toString().trim() !== '' || ["ISNULL", "NOTNULL"].includes(criteria.operator?.KEY);

            return hasAttribute && hasOperator && hasValue;
        });

        if (validCriteria.length === 0) {
            return;
        }
        else {
            // format search criteria for the API   
            let formattedCriteria = validCriteria.map(criteria => {                

                let val = {
                    value: criteria.selectedValue ?? [''],
                    type: Array.isArray(criteria.selectedValue) ? 'array' : typeof criteria.selectedValue === "object" ? 'array' : typeof criteria.selectedValue
                };
                return {
                    attribute: criteria.selectedAttribute.key,
                    operator: criteria.operator.KEY,
                    value: JSON.stringify(val)
                };
            });

            // Reset pagination to first page when doing a new search
            setPaginationModel({ pageIndex: 0, pageSize: paginationModel.pageSize });
            
            console.log("formattedCriteria: ", formattedCriteria)

            // Set the cached criteria - this will trigger the useEffect above
            setCachedSearchCriteria(formattedCriteria);
        }
    }


    // Skeleton component for individual rows
    const SkeletonAttributeRow = () => (
        <Mui.TableRow style={{ borderTop: '1px solid lightgray', borderBottom: '1px solid lightgray' }}>
            <Mui.TableCell>
                <Mui.Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            </Mui.TableCell>
            <Mui.TableCell style={{ borderBottom: 'none' }}>
                <Mui.Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            </Mui.TableCell>
            <Mui.TableCell align="center" style={{ borderBottom: 'none' }}>
                <Mui.Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            </Mui.TableCell>
            <Mui.TableCell align="center" style={{ borderBottom: 'none' }}>
                <Mui.Skeleton variant="circular" width={40} height={40} />
            </Mui.TableCell>
        </Mui.TableRow>
    );

    if (attributesLoading) {
        return (
            <>
                <Mui.TableContainer>
                    <Mui.Table sx={{ minWidth: 650 }} aria-label="Search criteria">
                        <Mui.TableHead>
                            <Mui.TableRow>
                                <Mui.TableCell sx={{ width: '40%' }}>
                                    <Mui.Typography style={{ fontWeight: 'bold' }}>Attributes</Mui.Typography>
                                </Mui.TableCell>
                                <Mui.TableCell align="center" sx={{ width: '25%' }}>
                                    <Mui.Typography style={{ fontWeight: 'bold' }}>Operator</Mui.Typography>
                                </Mui.TableCell>
                                <Mui.TableCell align="center" sx={{ width: '25%' }}>
                                    <Mui.Typography style={{ fontWeight: 'bold' }}>Value</Mui.Typography>
                                </Mui.TableCell>
                                <Mui.TableCell align="center" sx={{ width: '10%' }}>
                                    <Mui.Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                                </Mui.TableCell>
                            </Mui.TableRow>
                        </Mui.TableHead>
                        <Mui.TableBody>
                            {[...Array(3)].map((_, index) => (
                                <SkeletonAttributeRow key={index} />
                            ))}
                        </Mui.TableBody>
                    </Mui.Table>
                </Mui.TableContainer>
                <Mui.Box display="flex" justifyContent="flex-end" p={2}>
                    <Mui.Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                </Mui.Box>
                <Mui.Box className="margin-top-2">
                    <Mui.Skeleton variant="rectangular" height={4} sx={{ borderRadius: 1, mb: 2 }} />
                    <Mui.Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </Mui.Box>
            </>
        );
    }

    return (
        <>
            <Mui.TableContainer>
                <Mui.Table sx={{ minWidth: 650 }} aria-label="Search criteria">
                    <Mui.TableHead>
                        <Mui.TableRow>
                            <Mui.TableCell sx={{ width: '40%' }}>
                                <Mui.Typography style={{ fontWeight: 'bold' }}>Attributes</Mui.Typography>
                            </Mui.TableCell>
                            <Mui.TableCell align="center" sx={{ width: '25%' }}>
                                <Mui.Typography style={{ fontWeight: 'bold' }}>Operator</Mui.Typography>
                            </Mui.TableCell>
                            <Mui.TableCell align="center" sx={{ width: '25%' }}>
                                <Mui.Typography style={{ fontWeight: 'bold' }}>Value</Mui.Typography>
                            </Mui.TableCell>
                            <Mui.TableCell align="center" sx={{ width: '10%' }}>
                                <Mui.Button
                                    aria-label="Add new search criteria row"
                                    size="small"
                                    onClick={addNewRow}
                                    startIcon={<MuiIcons.Add fontSize="small" color={'primary'} />}
                                >
                                    <Mui.Typography style={{ fontWeight: 'bold' }}>Add New</Mui.Typography>
                                </Mui.Button>
                            </Mui.TableCell>
                        </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                        {/* First row - always visible */}
                        <AttributeRow
                            key={attributeRows[0].id}
                            rowIndex={attributeRows[0].index}
                            attributesData={filteredAttributes}
                            activeProgram={activeProgram}
                            updateRowData={updateRowData}
                            onSearch={handleSearch}
                            initialData={searchCriteria[0]}
                        />

                        {/* Additional rows - grouped in a single accordion */}
                        {attributeRows.length > 1 && (
                            <Mui.TableRow>
                                <Mui.TableCell colSpan={4} sx={{ padding: 0, border: 'none' }}>
                                    <Mui.Accordion defaultExpanded>
                                        <Mui.AccordionSummary
                                            expandIcon={<MuiIcons.ExpandMore />}
                                            aria-controls="additional-criteria-content"
                                            id="additional-criteria-header"
                                            sx={{
                                                padding: '1rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Mui.Typography sx={{ mr: 1 }}>
                                                Additional Search Criteria ({attributeRows.length - 1} row{attributeRows.length - 1 !== 1 ? 's' : ''})
                                            </Mui.Typography>
                                        </Mui.AccordionSummary>
                                        <Mui.AccordionDetails sx={{ padding: 0 }}>
                                            <Mui.Table sx={{ width: '100%' }}>
                                                <Mui.TableHead sx={{ visibility: 'hidden', height: 0 }}>
                                                    <Mui.TableRow>
                                                        <Mui.TableCell sx={{ width: '40%', padding: 0, border: 'none' }}></Mui.TableCell>
                                                        <Mui.TableCell sx={{ width: '25%', padding: 0, border: 'none' }}></Mui.TableCell>
                                                        <Mui.TableCell sx={{ width: '25%', padding: 0, border: 'none' }}></Mui.TableCell>
                                                        <Mui.TableCell sx={{ width: '10%', padding: 0, border: 'none' }}></Mui.TableCell>
                                                    </Mui.TableRow>
                                                </Mui.TableHead>
                                                <Mui.TableBody>
                                                    {attributeRows.slice(1).map((row) => (
                                                        <AttributeRow
                                                            key={row.id}
                                                            rowIndex={row.index}
                                                            attributesData={filteredAttributes}
                                                            activeProgram={activeProgram}
                                                            deleteRow={() => deleteRow(row.id)}
                                                            updateRowData={updateRowData}
                                                            onSearch={handleSearch}
                                                            initialData={searchCriteria[row.index]}
                                                        />
                                                    ))}
                                                </Mui.TableBody>
                                            </Mui.Table>
                                        </Mui.AccordionDetails>
                                    </Mui.Accordion>
                                </Mui.TableCell>
                            </Mui.TableRow>
                        )}
                    </Mui.TableBody>
                </Mui.Table>
            </Mui.TableContainer>
            <Mui.Box display="flex" justifyContent="space-between" p={2}>
                <Mui.Box>
                    <Mui.Button 
                        variant="outlined" 
                        color={showSaveSuccess ? "success" : "primary"} 
                        onClick={saveSearchCriteria} 
                        sx={{ mr: 1 }}
                        startIcon={showSaveSuccess ? <MuiIcons.Check /> : null}
                    >
                        {showSaveSuccess ? "Saved" : "Save Search"}
                    </Mui.Button>
                    <Mui.Button variant="outlined" color="primary" onClick={loadSearchCriteria}>
                        Load Search
                    </Mui.Button>
                </Mui.Box>
                <Mui.Button variant="contained" color="primary" onClick={handleSearch}>
                    Search
                </Mui.Button>
            </Mui.Box>
            <Mui.Box className="margin-top-2">
                <Mui.LinearProgress sx={{ visibility: (allDataLoading ? undefined : "hidden" ) }} />
                <ResultsGrid
                    searchCriteria={searchCriteria}
                    searchParams={searchParams}
                    clearSearchCriteria={clearSearchCriteria}
                    clearGrid={clearGrid}
                    paginationModel={paginationModel}
                    sortModel={sortModel}
                    setPaginationModel={setPaginationModel}
                    setSortModel={setSortModel}
                    onRefresh={onRefresh}
                    canRefresh={!!searchParams && !allDataLoading}
                />
            </Mui.Box>
        </>
    );
}

function getInitialSearchCriteriaValue() {
    return {
        0: {
            selectedAttribute: { key: "programId", label: "ProgramId", type: "text" },
            operator: SEARCH_OPERATORS.text.find(o => o.KEY === "EQ"),
            selectedValue: [ getProgramIdFromPath(), ]
        }
    };
}

export default AdvancedSearch;